const PREVIEWABLE_IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "webp", "svg"]);
const PREVIEWABLE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "webp", "svg", "pdf"]);
const STOP_WORDS = new Set([
  "art",
  "artwork",
  "binated",
  "design",
  "file",
  "final",
  "image",
  "logo",
  "mockup",
  "proof",
  "rev",
  "revision",
  "the",
]);

export const normalizeMatchTokens = (...values) => {
  const tokens = values
    .flatMap((value) => {
      if (value == null) return [];
      return String(value)
        .toLowerCase()
        .replace(/\.[a-z0-9]{2,5}(?=$|[?#])/g, " ")
        .replace(/[^a-z0-9]+/g, " ")
        .split(/\s+/)
        .map((token) => token.trim())
        .filter(Boolean);
    })
    .filter((token) => !STOP_WORDS.has(token));
  return [...new Set(tokens)];
};

export const getFilePreviewKind = (file = {}) => {
  const extension = String(file?.extension || "").trim().toLowerCase();
  if (PREVIEWABLE_IMAGE_EXTENSIONS.has(extension)) return "image";
  if (extension === "pdf") return "pdf";
  return "link";
};

export const isPreviewableFile = (file = {}) => {
  const extension = String(file?.extension || "").trim().toLowerCase();
  if (PREVIEWABLE_EXTENSIONS.has(extension)) return true;
  return Boolean(file?.isImage || file?.isPdf);
};

export const getArtworkSelectionKey = (artwork = {}) =>
  [
    String(artwork?.artworkId ?? ""),
    String(artwork?.orderItemId ?? ""),
    String(artwork?.decorationId ?? ""),
  ].join("::");

export const scoreArtworkFileMatch = (artwork = {}, file = {}) => {
  const artworkTokens = normalizeMatchTokens(
    artwork?.logoName,
    artwork?.imprintType,
    artwork?.location,
    artwork?.originalImage
  );
  const fileTokens = normalizeMatchTokens(file?.fileName, file?.zipEntryPath);
  if (!artworkTokens.length || !fileTokens.length) {
    return { score: 0, reasons: [] };
  }

  const sharedTokens = artworkTokens.filter((token) => fileTokens.includes(token));
  let score = sharedTokens.length * 2;
  const reasons = [];

  if (sharedTokens.length) {
    reasons.push(`Shared tokens: ${sharedTokens.join(", ")}`);
  }

  const logoName = String(artwork?.logoName || "").trim().toLowerCase();
  const fileStem = String(file?.fileName || "")
    .trim()
    .toLowerCase()
    .replace(/\.[a-z0-9]{2,5}$/i, "");
  if (logoName && fileStem && (logoName === fileStem || fileStem.includes(logoName.replace(/[^a-z0-9]+/g, " ").trim()))) {
    score += 6;
    reasons.push("Logo name aligns with file name");
  }

  const locationTokens = normalizeMatchTokens(artwork?.location);
  const matchedLocationTokens = locationTokens.filter((token) => fileTokens.includes(token));
  if (matchedLocationTokens.length) {
    score += 4;
    reasons.push(`Location matched: ${matchedLocationTokens.join(", ")}`);
  }

  const imprintTokens = normalizeMatchTokens(artwork?.imprintType);
  const matchedImprintTokens = imprintTokens.filter((token) => fileTokens.includes(token));
  if (matchedImprintTokens.length) {
    score += 2;
    reasons.push(`Imprint matched: ${matchedImprintTokens.join(", ")}`);
  }

  return { score, reasons };
};

export const autoMatchArtworkFiles = (artworks = [], files = []) => {
  const suggestions = {};
  const usedFileIds = new Set();

  (artworks || []).forEach((artwork) => {
    const ranked = (files || [])
      .map((file) => ({ file, ...scoreArtworkFileMatch(artwork, file) }))
      .filter((entry) => entry.score >= 5)
      .sort((a, b) => b.score - a.score);

    const best = ranked.find((entry) => !usedFileIds.has(entry.file?.fileId)) || ranked[0];
    const next = ranked[1] || null;
    const key = getArtworkSelectionKey(artwork);

    if (!best) {
      suggestions[key] = {
        sourceFileId: "",
        confidence: "none",
        score: 0,
        reasons: ["No high-confidence file match found."],
      };
      return;
    }

    usedFileIds.add(best.file?.fileId);
    const confidence = best.score >= 10 ? "high" : best.score >= 7 ? "medium" : "low";
    const reasons = [...best.reasons];
    if (next && best.score - next.score <= 2) {
      reasons.push("Close alternate match detected; review before saving.");
    }

    suggestions[key] = {
      sourceFileId: best.file?.fileId || "",
      confidence,
      score: best.score,
      reasons,
    };
  });

  return suggestions;
};

export const findDuplicateSelections = (selectionEntries = []) => {
  const counts = new Map();
  (selectionEntries || []).forEach((entry) => {
    const sourceFileId = String(entry?.sourceFileId || "").trim();
    if (!sourceFileId) return;
    counts.set(sourceFileId, (counts.get(sourceFileId) || 0) + 1);
  });
  return selectionEntries.filter((entry) => {
    const sourceFileId = String(entry?.sourceFileId || "").trim();
    return sourceFileId && (counts.get(sourceFileId) || 0) > 1;
  });
};
